import Button from '@pelckmans/elementis/components/button';
import Section from '@pelckmans/elementis/components/section';
import Upload from '@pelckmans/elementis/icons/upload';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleFilter from './exercise/filters/ModuleFilter';
import SchoolYearFilter from './exercise/filters/SchoolYearFilter';
import useTableData from './exercise/hooks/useTableData';
import ResultsTable from './table/ResultsTable';
import SchoolFilter from './exercise/filters/SchoolFilter';
import ResultsEmptyState from './per-student/components/ResultsEmptyState';
import { useFilters } from '../context/filter-context/FilterContext';
import { useSearchParams } from 'react-router-dom';
import { FilterSearchParam } from '../../../../shared-components/elementis/types/FilterSearchParam';

export default function ExerciseTab() {
  const resultsTableContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading,
    data,
    noGroups,
    noScorableItems,
    noGroupsForFilter,
    noScorableItemsForFilter,
    detailView,
    usedScorableItems,
  } = useTableData();

  const { selectedModule } = useFilters();

  if (!selectedModule) return null;

  const renderTable = () => {
    if (!loading && noGroups) return <ResultsEmptyState variant="no-groups" />;
    if (!loading && noScorableItems) {
      return <ResultsEmptyState variant="no-scorable-items" moduleNiceName={selectedModule.niceName} />;
    }

    return (
      <ResultsTable
        isLoading={loading}
        data={data}
        ref={resultsTableContainerRef}
        noGroups={noGroupsForFilter}
        noScorableItems={noScorableItemsForFilter}
        detailView={detailView}
      />
    );
  };

  const sectionTitle = detailView
    ? `${t('PAGES.RESULTS.PER_EXERCISE.SECTION_TITLE')}: ${usedScorableItems[0].name}`
    : t('PAGES.RESULTS.PER_EXERCISE.SECTION_TITLE');

  return (
    <Section
      title={sectionTitle}
      actionGroupAriaLabel={undefined}
      actionGroupRole={undefined}
      {...(detailView
        ? {
            onBackClick: () => {
              // TODO: This should just be browser back when all filters are implemented through url
              const newParams = new URLSearchParams(searchParams);
              newParams.delete(FilterSearchParam.Exercises);

              setSearchParams(newParams);
            },
            backButtonLabel: t('PAGES.RESULTS.BACK_BUTTON_A11Y_LABEL'),
          }
        : {})}
      filter={
        noGroups || noScorableItems || detailView || loading ? undefined : (
          <>
            <ModuleFilter key="module-filter" />
            <SchoolYearFilter key="school-year-filter" />
            <SchoolFilter key="school-filter" />
          </>
        )
      }
      actions={
        noGroups || noScorableItems || loading ? null : (
          <Button prominence="secondary" size="sm" startIcon={<Upload data-testid="export-icon" />}>
            {t('PAGES.RESULTS.EXPORT')}
          </Button>
        )
      }
    >
      <div className="results-table-container" ref={resultsTableContainerRef}>
        {renderTable()}
      </div>
    </Section>
  );
}
